import { useRef, useState, useCallback } from 'react';

interface CoverageItem {
  name: string;
  subtitle: string;
  image: string;
  label?: string;
}

const coverageItems: CoverageItem[] = [
  {
    name: 'Jewelry & Watches',
    subtitle: 'Insured transport with tamper-evident packaging and real-time tracking',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20jewelry%20displayed%20elegantly%20on%20a%20dark%20velvet%20surface%20with%20diamond%20necklace%20and%20gold%20watch%2C%20warm%20spotlight%20illumination%2C%20premium%20product%20photography%2C%20dark%20atmospheric%20background%20with%20soft%20golden%20bokeh%2C%20refined%20luxurious%20aesthetic%2C%20clean%20minimalist%20composition&width=600&height=800&seq=coverage-jewelry-01&orientation=portrait',
    label: 'Strict Limits Apply',
  },
  {
    name: 'Precious Metals',
    subtitle: 'Armored transit with vault-to-vault chain-of-custody protocols',
    image: 'https://readdy.ai/api/search-image?query=Stacked%20polished%20gold%20bars%20on%20a%20dark%20surface%20with%20dramatic%20side%20lighting%20creating%20rich%20highlights%2C%20professional%20financial%20photography%2C%20deep%20shadows%20and%20warm%20metallic%20reflections%2C%20premium%20banking%20aesthetic%2C%20clean%20minimalist%20composition%20with%20negative%20space&width=600&height=800&seq=coverage-metals-02&orientation=portrait',
    label: 'Armed Required',
  },
  {
    name: 'Fine Art & Antiques',
    subtitle: 'Climate-controlled, custom-crated, museum-grade handling throughout',
    image: 'https://readdy.ai/api/search-image?query=Classical%20oil%20painting%20in%20ornate%20gold%20frame%20being%20carefully%20wrapped%20in%20protective%20material%20inside%20a%20gallery%20setting%2C%20soft%20gallery%20lighting%2C%20professional%20art%20handling%20scene%2C%20warm%20neutral%20tones%2C%20refined%20and%20elegant%20atmosphere%2C%20clean%20professional%20photography&width=600&height=800&seq=coverage-art-03&orientation=portrait',
    label: 'Custom Crating',
  },
  {
    name: 'Legal & Financial Docs',
    subtitle: 'Same-day international dispatch with notarized chain-of-custody',
    image: 'https://readdy.ai/api/search-image?query=Sealed%20confidential%20legal%20documents%20with%20red%20wax%20seal%20and%20ribbon%20on%20a%20dark%20mahogany%20desk%2C%20warm%20desk%20lamp%20lighting%2C%20professional%20law%20office%20atmosphere%2C%20leather-bound%20folders%2C%20sophisticated%20and%20serious%20mood%2C%20clean%20composition%20with%20deep%20shadows&width=600&height=800&seq=coverage-legal-04&orientation=portrait',
    label: 'Rush Available',
  },
  {
    name: 'Electronics & Tech',
    subtitle: 'Anti-static, shock-proof packaging with full declared-value coverage',
    image: 'https://readdy.ai/api/search-image?query=Premium%20electronic%20device%20being%20placed%20into%20a%20custom%20protective%20foam%20case%20on%20a%20clean%20white%20surface%2C%20modern%20tech%20aesthetic%2C%20soft%20studio%20lighting%2C%20minimalist%20product%20photography%2C%20clean%20professional%20composition%2C%20silver%20and%20white%20tones&width=600&height=800&seq=coverage-electronics-05&orientation=portrait',
    label: 'Anti-Static',
  },
  {
    name: 'Medical & Scientific',
    subtitle: 'Temperature-controlled, IATA-compliant biological sample transport',
    image: 'https://readdy.ai/api/search-image?query=Medical%20transport%20container%20with%20temperature%20monitor%20in%20a%20clean%20laboratory%20setting%2C%20sterile%20white%20environment%20with%20soft%20blue-white%20lighting%2C%20professional%20healthcare%20logistics%20aesthetic%2C%20clean%20minimalist%20photography%2C%20clinical%20precision%20atmosphere&width=600&height=800&seq=coverage-medical-06&orientation=portrait',
    label: 'Temp-Controlled',
  },
];

export default function CoverageScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  return (
    <section id="coverage" className="relative bg-background-100 py-20 md:py-28">
      <div className="px-6 md:px-10 lg:px-16 mb-12 md:mb-16">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row md:items-end justify-between gap-4">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
            Global Coverage,<br />
            <span className="text-primary-500">Every Category</span>
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-md">
            From jewelry under strict limits to art requiring museum-grade handling — every shipment class has a dedicated protocol.
          </p>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto">
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 md:gap-5 overflow-x-auto px-6 md:px-10 lg:px-16 pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {coverageItems.map((item, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[260px] md:w-[300px] lg:w-[320px] snap-start rounded-2xl overflow-hidden group cursor-pointer relative"
            >
              <div className="relative h-[360px] md:h-[400px]">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20"></div>
                {item.label && (
                  <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold tracking-wide border border-white/20">
                    {item.label}
                  </span>
                )}
                <div className="absolute bottom-0 left-0 p-5 w-full">
                  <h4 className="text-lg md:text-xl font-heading font-bold text-white mb-1.5">{item.name}</h4>
                  <p className="text-white/70 text-xs md:text-sm leading-relaxed">{item.subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 md:px-10 lg:px-16 mt-6">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center border border-foreground-200 text-foreground-600 hover:bg-foreground-100 hover:text-foreground-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center border border-foreground-200 text-foreground-600 hover:bg-foreground-100 hover:text-foreground-950 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}